/**
 * The thread's data, through the registry - same rule as `useConversations`.
 *
 * ⛔ **AND THIS HOOK IS THE ONE THE REGISTRY'S DESIGN WAS ARGUED AGAINST.**
 * `query-store.ts` rejects a narrow refetch in these words: *"the tempting
 * narrow refetch - just the open conversation - is wrong in exactly the case
 * that matters, and it looks correct in every test that has a conversation
 * open."* Until now nothing HAD a conversation open, so the wrong version would
 * have passed everything. This is the second entry in the refetch set, and the
 * first one that makes the argument checkable.
 *
 * ⚠️ THE KEY CARRIES THE CONVERSATION ID, so switching threads materialises a
 * new entry and releases the old one. One key for all threads would make the
 * store's refcount lie about what is on screen, and a reconnect would refetch a
 * thread nobody is reading - the leak `query-store.ts` deletes entries to avoid.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { refetchKey, subscribe } from '../data/query-store.js'
import { type ThreadPage, MAX_PAGES, fetchThreadPages } from '../data/inbox-api.js'
import type { PinnedRow, ThreadMessage } from '../../../src/services/thread.js'

export type UseThread = {
  /** `null` until the first response. NOT the same as an empty array. */
  rows: ThreadMessage[] | null
  /** The conversation's pinned rows, both kinds, newest pin first. */
  pinned: PinnedRow[]
  /**
   * ⛔ THIS FIELD AND ITS PLUMBING LANDED 2026-08-08, WITH THE SAME FINDING
   * BEHIND IT AS `useConversations`': `ThreadView` renders *"Could not load this
   * conversation"* behind an `error` prop **that nothing could ever set**.
   *
   * ⚠️ THREE COMPONENTS HAD THIS AT ONCE - the list, the thread and the panel -
   * because the third and fourth were written by copying the first, **and the
   * defect was in the part that was copied.** A rendered state with no producer
   * propagates exactly as fast as the code around it.
   */
  error: string | null
  /** Ask for one more page of older messages. A no-op at the end. */
  loadMore: () => void
  /**
   * ⛔ FOUR STATES, THE SAME AS THE LIST'S AND FOR THE SAME REASON. `end` is the
   * server saying this conversation has no older messages; `capped` is us
   * refusing to walk further. Reporting our ceiling as the start of the
   * conversation would be a wrong answer delivered confidently.
   */
  more: 'available' | 'loading' | 'end' | 'capped'
}

/**
 * `null` conversation id means no thread is open, which is a REAL state - the
 * inbox with nothing selected - and not a missing argument.
 *
 * ⛔ IT MUST NOT SUBSCRIBE IN THAT CASE. A `thread:null` entry would sit in the
 * refetch set forever, and every reconnect would issue a request for a
 * conversation that does not exist.
 */
export function useThread(conversationId: string | null): UseThread {
  const [rows, setRows] = useState<ThreadMessage[] | null>(null)
  /** ⛔ Server-sent, and NOT derived from `rows`: a pin older than the loaded
   *  range is exactly the one the region exists to surface. */
  const [pinned, setPinned] = useState<PinnedRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  // See `useConversations` for why this is a ref rather than state: the fetcher
  // is captured once and must read the CURRENT count when a reconnect re-runs it.
  const pagesRef = useRef(1)
  const key = conversationId === null ? null : `thread:${conversationId}`

  useEffect(() => {
    /**
     * ⛔ CLEARED FIRST, EVERY TIME. Without this, opening conversation B shows
     * A's messages under B's header until the fetch returns - a wrong answer
     * displayed confidently, and worse here than in the list because the two
     * threads belong to different people. `useConversations` carries the same
     * line for the view switch, and this is the case that makes it visible.
     */
    setRows(null)
    setPinned([])
    setError(null)
    pagesRef.current = 1
    setCursor(null)
    setLoadingMore(false)
    if (conversationId === null || key === null) return

    return subscribe<ThreadPage>(
      key,
      () => fetchThreadPages(conversationId, pagesRef.current),
      (page) => {
        setRows(page.messages)
        setPinned(page.pinned)
        setCursor(page.cursor)
        setLoadingMore(false)
        setError(null)
      },
      (err) => {
        setError(err instanceof Error ? err.message : 'Unknown error')
        setLoadingMore(false)
      },
    )
  }, [conversationId, key])

  const more: UseThread['more'] =
    loadingMore ? 'loading' : cursor === null ? 'end' : pagesRef.current >= MAX_PAGES ? 'capped' : 'available'

  const loadMore = useCallback(() => {
    if (more !== 'available' || key === null) return
    pagesRef.current += 1
    setLoadingMore(true)
    void refetchKey(key)
  }, [key, more])

  return { rows, pinned, error, loadMore, more }
}
